/**
 * A route is declared ONCE, here, in `@bat/shared`, and everything else is meant to read it: the backend to register
 * it and validate the request from it, the frontend to infer the types of its input and output from it, and a test to
 * parse a real payload against it. Nobody writes the shape of a response by hand again.
 *
 * `defineRoute` checks the declaration when it is DEFINED (at import time, so a broken contract never loads):
 * the id, the `/api/` prefix, that the path parameters and `params` agree (and `params` and `query` are object
 * schemas with every field typed), that a GET has no body, that a request body is strict at every level and bounded
 * (a free document in a body DECLARES `maxBytes`; nothing enforces it yet, so `registerContractRoute` refuses to register such a contract) and that the response is CLOSED and JSON (see `closed-schema.ts`; the
 * only way to accept a free document is `jsonDocument`). `/api/screens/` belongs to `defineScreen`. Every error names
 * the route id it was given and never a data value.
 *
 * Types: a `z.coerce.*` field must declare its input (`z.coerce.number<number>()`); one that does not is a type error
 * that names the field (at runtime the two are the same object). A contract carries a type-only brand, so an object
 * that merely has the same keys is not a `RouteContract`.
 *
 * It registers the contract in `routeContracts`, so two routes cannot share an id or a method and path.
 */

import { z } from 'zod/v4'

import { describeOpenSchemaFindings, findOpenSchemas } from './closed-schema.js'
import {
  API_BODY_LIMIT_BYTES,
  MAX_ROUTE_BODY_LIMIT_BYTES,
  jsonDocumentMaxBytes,
} from './json-document.js'

/** Type-only (no value exists at runtime): only `defineRoute` produces something that carries it. */
declare const routeContractBrand: unique symbol

const API_PATH_PREFIX = '/api/'

export type RouteMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** The preHandler chain the route is meant to be mounted with. Finer permissions are extra guards at registration. */
export type RouteAccess = 'authenticated' | 'org_admin' | 'super_admin'

export type RouteSuccessStatus = 200 | 201 | 202

type ObjectSchema = z.ZodObject<z.core.$ZodShape, z.core.$ZodObjectConfig>

/**
 * A route that answers a FILE instead of a JSON envelope (a download). The server checks the type of the file it sends
 * against `contentTypes` and sets `Content-Type` and `Content-Disposition`; the client gets a `RouteFile`.
 */
export interface RouteFileResponse {
  readonly kind: 'file'
  /** MIME types without parameters: `text/csv`. */
  readonly contentTypes: readonly [string, ...string[]]
}

/** What a client receives from a file route (`callContract`): the blob and what the headers said. */
export interface RouteFile {
  readonly fileName: string | null
  readonly contentType: string
}

/** True for a file descriptor, false for a zod schema (a schema is never a descriptor, whatever it is called). */
export function isFileResponse(
  response: z.ZodType | RouteFileResponse,
): response is RouteFileResponse {
  return !('_zod' in response) && (response as { kind?: unknown }).kind === 'file'
}

export interface RouteContractInput {
  /** Dot-separated segments: `audit.exports.list`. */
  id: string
  method: RouteMethod
  /** Fastify syntax, with the prefix: `/api/admin/system/audit/exports/:id`. */
  path: `/api/${string}`
  access: RouteAccess
  params?: ObjectSchema
  query?: ObjectSchema
  body?: z.ZodType
  /** The body limit of THIS route, in bytes (`API_BODY_LIMIT_BYTES` unless declared; needs a `body`). */
  bodyLimitBytes?: number
  /** The `data` of the success envelope (see `okEnvelope`), or a file (`{ kind: 'file', contentTypes }`, GET only). */
  response: z.ZodType | RouteFileResponse
  /** 200 unless declared. */
  status?: RouteSuccessStatus
  summary: string
  tags: readonly string[]
}

/**
 * The schema a route declares under `K`: the schema when the input has it, `undefined` when it has none, and
 * `S | undefined` when the key is OPTIONAL in the type of the input (a conditional spread, an input widened to
 * `RouteContractInput`): the type cannot say which it is, and the contract does not claim a schema that may not exist.
 */
type DeclaredSchema<I, K extends 'params' | 'query' | 'body'> = K extends keyof I
  ? undefined extends I[K]
    ? Exclude<I[K], undefined> | undefined
    : I[K]
  : undefined

export interface RouteContract<I extends RouteContractInput = RouteContractInput> {
  readonly [routeContractBrand]: true
  readonly id: I['id']
  readonly method: I['method']
  readonly path: I['path']
  readonly access: I['access']
  readonly params: DeclaredSchema<I, 'params'>
  readonly query: DeclaredSchema<I, 'query'>
  readonly body: DeclaredSchema<I, 'body'>
  /** The body limit the route is mounted with: `bodyLimitBytes` or `API_BODY_LIMIT_BYTES`. */
  readonly bodyLimitBytes: number
  readonly response: I['response']
  readonly status: I extends { status: infer S extends RouteSuccessStatus } ? S : 200
  readonly summary: string
  readonly tags: readonly string[]
}

/**
 * What a function that accepts ANY route contract takes. It is written out (not `RouteContract<RouteContractInput>`)
 * because computed from it `params`, `query` and `body` would be the schemas without `undefined`, and a contract that
 * does not declare one of them would not be assignable.
 */
export interface AnyRouteContract {
  readonly [routeContractBrand]: true
  readonly id: string
  readonly method: RouteMethod
  readonly path: `/api/${string}`
  readonly access: RouteAccess
  readonly params: ObjectSchema | undefined
  readonly query: ObjectSchema | undefined
  readonly body: z.ZodType | undefined
  readonly bodyLimitBytes: number
  readonly response: z.ZodType | RouteFileResponse
  readonly status: RouteSuccessStatus
  readonly summary: string
  readonly tags: readonly string[]
}

/**
 * The success envelope of every route: `{ success: true, data, message? }`, closed (any other key is an error). The
 * `message` is the one the handlers send with a success (`api.service.ts` reads it for the toast).
 */
export function okEnvelope<T extends z.ZodType>(
  data: T,
): z.ZodObject<
  { success: z.ZodLiteral<true>; data: T; message: z.ZodOptional<z.ZodString> },
  z.core.$strict
> {
  return z.strictObject({ success: z.literal(true), data, message: z.string().optional() })
}

const ROUTE_ID_PATTERN = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/
const PATH_PARAMETER_PATTERN = /:([A-Za-z_][A-Za-z0-9_]*)/g
/** A MIME type with no parameters: `text/csv`, `application/json`. */
const CONTENT_TYPE_PATTERN = /^[a-z]+\/[a-z0-9.+-]+$/
/** The same prefix as `SCREEN_PATH_PREFIX` (`defineScreen` cannot be imported from here: it imports this module). */
const RESERVED_SCREEN_PREFIX = '/api/screens/'

const contractsById = new Map<string, AnyRouteContract>()
const idsByMethodAndPath = new Map<string, string>()

/** Every contract defined so far, by id. */
export const routeContracts: ReadonlyMap<string, AnyRouteContract> = contractsById

function shapeKeys(schema: ObjectSchema | undefined): string[] {
  return schema ? Object.keys(schema.shape) : []
}

function assertObjectSchema(
  id: string,
  slot: 'params' | 'query',
  schema: z.ZodType | undefined,
): void {
  if (schema !== undefined && schema._zod.def.type !== 'object') {
    throw new Error(`defineRoute ${id}: ${slot} must be an object schema`)
  }
}

/**
 * The fields of `params` and `query` whose input type is `unknown` (a `z.coerce.*` that did not declare it), by name.
 * A widened schema (`string` keys) says nothing and is ignored.
 */
export type UntypedInputFields<S> = S extends ObjectSchema
  ? string extends keyof S['shape']
    ? never
    : {
        [K in keyof S['shape'] & string]: unknown extends z.input<S['shape'][K]> ? K : never
      }[keyof S['shape'] & string]
  : never

type UntypedRouteInputFields<I extends RouteContractInput> =
  | UntypedInputFields<DeclaredSchema<I, 'params'>>
  | UntypedInputFields<DeclaredSchema<I, 'query'>>

type RouteInputCheck<I extends RouteContractInput> = [UntypedRouteInputFields<I>] extends [never]
  ? unknown
  : { readonly __untypedInputFields: UntypedRouteInputFields<I> }

/** Declares a route. */
export function defineRoute<const I extends RouteContractInput>(
  input: I & RouteInputCheck<I>,
): RouteContract<I> {
  return registerRoute<I>(input, 'route')
}

/**
 * The route of a screen: the same as `defineRoute`, and the only one allowed under `/api/screens/`. It is NOT in the
 * barrel of the package; `defineScreen` is its one caller.
 */
export function defineScreenRoute<const I extends RouteContractInput>(
  input: I & RouteInputCheck<I>,
): RouteContract<I> {
  return registerRoute<I>(input, 'screen')
}

function registerRoute<I extends RouteContractInput>(
  input: I,
  owner: 'route' | 'screen',
): RouteContract<I> {
  const { id, method, path } = input

  if (!ROUTE_ID_PATTERN.test(id)) {
    throw new Error(
      `defineRoute: invalid id "${id}" (expected dot-separated segments such as "audit.exports.list")`,
    )
  }
  if (!path.startsWith(API_PATH_PREFIX)) {
    throw new Error(`defineRoute ${id}: path "${path}" must start with ${API_PATH_PREFIX}`)
  }
  if (owner === 'route' && `${path}/`.startsWith(RESERVED_SCREEN_PREFIX)) {
    throw new Error(
      `defineRoute ${id}: ${RESERVED_SCREEN_PREFIX} is reserved for defineScreen (a screen always carries freshness and regions)`,
    )
  }

  if (owner === 'route' && id.startsWith('screens.')) {
    throw new Error(`defineRoute ${id}: the id prefix "screens." is reserved for defineScreen`)
  }

  assertObjectSchema(id, 'params', input.params)
  assertObjectSchema(id, 'query', input.query)

  for (const slot of ['params', 'query'] as const) {
    const schema = input[slot]
    if (schema === undefined) continue
    // Not strict (Fastify coerces the query and drops the keys nobody declared), but every field has a type.
    const open = findOpenSchemas(schema, { root: slot, closure: 'closed' })
    if (open.length > 0) {
      throw new Error(
        `defineRoute ${id}: open ${slot} schema at ${describeOpenSchemaFindings(open)}; declare the type of every field`,
      )
    }
  }

  const declaredParams = shapeKeys(input.params)
  const pathParams = [...path.matchAll(PATH_PARAMETER_PATTERN)].map((match) => match[1])
  for (const name of pathParams) {
    if (pathParams.indexOf(name) !== pathParams.lastIndexOf(name)) {
      throw new Error(`defineRoute ${id}: path parameter ":${name}" appears more than once`)
    }
    if (!declaredParams.includes(name)) {
      throw new Error(`defineRoute ${id}: path parameter ":${name}" has no schema in params`)
    }
  }
  for (const key of declaredParams) {
    if (!pathParams.includes(key)) {
      throw new Error(`defineRoute ${id}: params key "${key}" is not in the path "${path}"`)
    }
  }

  if (input.bodyLimitBytes !== undefined) {
    if (input.body === undefined) {
      throw new Error(`defineRoute ${id}: bodyLimitBytes needs a body`)
    }
    if (
      !Number.isSafeInteger(input.bodyLimitBytes) ||
      input.bodyLimitBytes < 1 ||
      input.bodyLimitBytes > MAX_ROUTE_BODY_LIMIT_BYTES
    ) {
      throw new Error(
        `defineRoute ${id}: bodyLimitBytes must be a positive integer up to ${MAX_ROUTE_BODY_LIMIT_BYTES}`,
      )
    }
  }
  const bodyLimitBytes = input.bodyLimitBytes ?? API_BODY_LIMIT_BYTES

  if (input.body !== undefined) {
    if (method === 'GET') {
      throw new Error(`defineRoute ${id}: a GET route cannot declare a body`)
    }
    // A document cannot declare more than the body the route accepts: the body limit is what really stops it. The
    // same walk finds the ones that do.
    const tooBig: Array<{ path: string; maxBytes: number }> = []
    const loose = findOpenSchemas(input.body, {
      root: 'body',
      closure: 'strict',
      documents: 'bounded',
      visitNode: (node, nodePath) => {
        const maxBytes = jsonDocumentMaxBytes(node)
        if (maxBytes !== undefined && maxBytes > bodyLimitBytes) {
          tooBig.push({ path: nodePath, maxBytes })
        }
      },
    })
    if (loose.length > 0) {
      throw new Error(
        `defineRoute ${id}: request body must be strict and bounded at ${describeOpenSchemaFindings(loose)}; use z.strictObject at every level and jsonDocument({ reason, maxBytes }) for a free document`,
      )
    }
    if (tooBig.length > 0) {
      const { path: documentPath, maxBytes } = tooBig[0]
      throw new Error(
        `defineRoute ${id}: ${documentPath} declares maxBytes ${maxBytes}, above the body limit of the route (${bodyLimitBytes} bytes)`,
      )
    }
  }

  let response: z.ZodType | RouteFileResponse = input.response
  if (isFileResponse(input.response)) {
    if (method !== 'GET') {
      throw new Error(`defineRoute ${id}: a file response requires method GET`)
    }
    const { contentTypes } = input.response
    if (contentTypes.length === 0) {
      throw new Error(`defineRoute ${id}: a file response declares at least one content type`)
    }
    for (const contentType of contentTypes) {
      if (!CONTENT_TYPE_PATTERN.test(contentType)) {
        throw new Error(`defineRoute ${id}: invalid content type "${contentType}"`)
      }
    }
    response = Object.freeze({
      kind: 'file',
      contentTypes: Object.freeze([
        ...contentTypes,
      ]) as unknown as RouteFileResponse['contentTypes'],
    })
  } else {
    const open = findOpenSchemas(input.response, {
      root: 'data',
      closure: 'closed',
      jsonOnly: true,
    })
    if (open.length > 0) {
      throw new Error(
        `defineRoute ${id}: open response schema at ${describeOpenSchemaFindings(open)}; declare every field with a JSON type (no Date, bigint, Map or Set) or use jsonDocument({ reason }) for a free JSON document`,
      )
    }
  }

  if (contractsById.has(id)) {
    throw new Error(`defineRoute: duplicate route id "${id}"`)
  }
  // Fastify treats `/x/:id` and `/x/:uuid`, and `/x` and `/x/`, as the same route.
  const routeKey = `${method} ${path.replace(PATH_PARAMETER_PATTERN, ':').replace(/\/+$/, '')}`
  const declaredBy = idsByMethodAndPath.get(routeKey)
  if (declaredBy !== undefined) {
    throw new Error(`defineRoute ${id}: ${method} ${path} is already declared by "${declaredBy}"`)
  }

  // The type is computed from `I`; the runtime shape is the same object, frozen, with every key present.
  const contract = Object.freeze({
    id,
    method,
    path,
    access: input.access,
    params: input.params,
    query: input.query,
    body: input.body,
    bodyLimitBytes,
    response,
    status: input.status ?? 200,
    summary: input.summary,
    tags: Object.freeze([...input.tags]),
  }) as unknown as RouteContract<I>

  contractsById.set(id, contract as unknown as AnyRouteContract)
  idsByMethodAndPath.set(routeKey, id)
  return contract
}

type InputPart<K extends string, S> = S extends z.ZodType
  ? { [P in K]: z.input<S> }
  : { [P in K]?: undefined }

type SchemaOutput<S> = S extends z.ZodType ? z.output<S> : undefined

/** The `data` a response schema produces, or the `RouteFile` a file response gives (distributes over a union). */
type ResponseOutput<R> = R extends z.ZodType
  ? z.output<R>
  : R extends RouteFileResponse
    ? RouteFile
    : never

export type RouteParams<C extends AnyRouteContract> = SchemaOutput<C['params']>
export type RouteQuery<C extends AnyRouteContract> = SchemaOutput<C['query']>
export type RouteBody<C extends AnyRouteContract> = SchemaOutput<C['body']>
export type RouteOutput<C extends AnyRouteContract> = ResponseOutput<C['response']>
export type RouteInput<C extends AnyRouteContract> = InputPart<'params', C['params']> &
  InputPart<'query', C['query']> &
  InputPart<'body', C['body']>
